import React, { ReactNode, useRef, useEffect, useState } from "react";
import usePosition from "../../Hooks/usePosition";
import styled from "styled-components";
import ResizeObserver from "resize-observer-polyfill";

const StyledStickyContainer = styled.div`
	transition: all 0.25s;
	background: transparent;
	width: 100%;

	&:not(.past-threshold) {
		.fixed-sticky {
			position: fixed;
		}
	}
`;

export interface StickyProps {
	children: ReactNode;
	className?: string;
	stick?: boolean;
	topOffset?: number;
	bottomOffset?: number;
	active?: boolean;
	keepVisible?: boolean;
	onHeightChange?: (height: number | undefined) => void;
	trigger?: number;
}

const Sticky: React.FC<StickyProps> = ({
	children,
	className,
	stick,
	topOffset = 0,
	bottomOffset = 0,
	active = true,
	keepVisible = false,
	onHeightChange,
	trigger,
}) => {
	const stickyContainer: any = useRef(null);
	const stickyContent: any = useRef(null);
	const [width, setWidth] = useState(stickyContainer.current ? stickyContainer.current.offsetWidth : 0);
	const [isFixed, setIsFixed] = useState(false);
	const [isSetting, setIsSetting] = useState(false);
	const [position, setPosition] = useState<"top" | "bottom">();
	const [preservedHeight, setPreservedHeight] = useState<number | string>();
	const [buttonWidth, setButtonWidth] = useState("");
	const [pastThreshold, setPastThreshold] = useState(false);
	const [contentHeight, setContentHeight] = useState<number>();
	const { top } = usePosition(stickyContainer);

	const processSticky = () => {
		if (!isSetting && active) {
			const containerTop = stickyContainer.current?.offsetTop;
			const containerWidth = stickyContainer.current?.offsetWidth;
			const stickyTop = containerTop - topOffset;
			const height = stickyContent.current?.offsetHeight;
			const stickyBottom = containerTop + height + bottomOffset;

			if (height !== contentHeight && Math.abs(height - (contentHeight ?? 0)) > 1) {
				setContentHeight(height);
			}

			if (width !== containerWidth) {
				setWidth(containerWidth);
			}

			const windowHeight = window.innerHeight;

			const scrollPoint = window.scrollY;

			if (keepVisible) {
				let stickyBottomThreshold = 0;
				if (height > windowHeight) {
					stickyBottomThreshold = containerTop + stickyContainer.current?.offsetHeight + bottomOffset;
				} else {
					stickyBottomThreshold =
						containerTop + stickyContainer.current?.offsetHeight - topOffset + windowHeight - height;
				}

				if (scrollPoint + windowHeight >= stickyBottom) {
					if (height > windowHeight) {
						//stick to bottom
						if (!isFixed) {
							setPreservedHeight(height);
							setIsSetting(true);
							setIsFixed(true);
							setPosition("bottom");
						} else if (position !== "bottom") {
							setPosition("bottom");
						}
					} else {
						//stick to top
						if (scrollPoint >= stickyTop) {
							if (!isFixed) {
								setPreservedHeight(height);
								setIsSetting(true);
								setIsFixed(true);
								setPosition("top");
							} else if (position !== "top") {
								setPosition("top");
							}
						} else {
							if (isFixed) {
								setIsSetting(true);
								setIsFixed(false);
								setPosition(undefined);
								setPreservedHeight(undefined);
							}
						}
					}
				} else {
					if (isFixed) {
						setIsSetting(true);
						setIsFixed(false);
						setPosition(undefined);
						setPreservedHeight(undefined);
					}
				}
				if (scrollPoint + windowHeight >= stickyBottomThreshold) {
					if (!pastThreshold) {
						setPastThreshold(true);
					}
				} else {
					if (pastThreshold) {
						setPastThreshold(false);
					}
				}
			} else {
				if (scrollPoint + windowHeight >= stickyBottom) {
					if (scrollPoint >= stickyTop) {
						if (!isFixed) {
							setPreservedHeight(height);
							setIsSetting(true);
							setIsFixed(true);
							setPosition("top");
						} else if (position !== "top") {
							setPosition("top");
						}
					} else {
						if (isFixed) {
							setIsSetting(true);
							setIsFixed(false);
							setPosition(undefined);
							setPreservedHeight(undefined);
						}
					}
				} else {
					if (!isFixed) {
						setPreservedHeight(height);
						setIsSetting(true);
						setIsFixed(true);
						setPosition("bottom");
					} else if (position !== "bottom") {
						setPosition("bottom");
					}
				}
			}
		}
	};

	useEffect(() => {
		processSticky();
	}, [top]);

	useEffect(() => {
		if (onHeightChange) {
			onHeightChange(contentHeight);
		}
	}, [contentHeight]);

	useEffect(() => {
		if (active) {
			const scrollListener = () => {
				processSticky();
			};

			window.addEventListener("scroll", scrollListener);

			const resizeObserver = new ResizeObserver(() => processSticky());

			// start observing a DOM node
			resizeObserver.observe(document.body);
			return () => {
				window.removeEventListener("scroll", scrollListener);
				resizeObserver.disconnect();
			};
		}
	}, [active, isSetting, pastThreshold, width]);

	useEffect(() => {
		setButtonWidth(`${width}px`);
	}, [stickyContainer, top, width]);

	useEffect(() => {
		if (isSetting) {
			setTimeout(() => {
				setIsSetting(false);
			}, 50);
		}
	}, [isFixed]);

	useEffect(() => {
		if (trigger && trigger > 0) {
			processSticky();
		}
	}, [trigger]);

	return (
		<StyledStickyContainer
			className={`sticky-container ${className} ${pastThreshold && position ? "past-threshold" : ""}`}
			ref={stickyContainer}
			style={{
				height: active ? preservedHeight : undefined,
				minHeight: active && keepVisible ? "100%" : undefined,
			}}
		>
			<div
				className={`sticky-inner-container ${(isFixed || stick) && active ? "fixed-sticky" : ""} ${
					position ? position : ""
				} ${!active ? "overflow-hidden" : ""}`}
				style={{ width: buttonWidth !== "0px" && (isFixed || stick) ? buttonWidth : undefined }}
				ref={stickyContent}
			>
				{children}
			</div>
		</StyledStickyContainer>
	);
};

export default Sticky;
